import { getQueryParams, roundTo } from '@/lib/client'
import type {
  TrackingConfirmationSchema,
  TrackingPaymentSchema,
} from '@/schemas/tracking.product'
import type { TrackingUserSchema } from '@/schemas/tracking.user'
import type { ICheckoutOrderSummaryItem } from '@/types/checkout'
import type { z } from 'zod'

const buildProductData = (item: ICheckoutOrderSummaryItem) => ({
  id: item._id,
  name: item.name,
  career: item.career,
  price: roundTo(item.discountedPrice || item.price, 2),
  scholarity: item.formation,
  institution: item.institution,
  role: item.role,
  status: item.situation,
  category: item.type,
})

const calculateTotals = (items: Array<ICheckoutOrderSummaryItem>) => {
  const price = items.reduce((a, b) => a + (b?.price || 0), 0)
  const discount = items.some((t) => t.discountedPrice)
    ? items.reduce((a, b) => a + (b?.price - (b?.discountedPrice || 0)), 0)
    : 0
  return {
    price,
    discount,
    itemsCount: items.length,
    avgPrice: price / items.length,
  }
}

export const posthog = {
  identify: (properties: z.infer<typeof TrackingUserSchema>) => {
    if (!window.posthog) return
    window.posthog.identify(properties.Id, {
      name: properties.Nome,
      email: properties.Email,
    })
  },
  checkoutImpression: ({
    items,
    campaign,
    link,
    coupon,
    upsell,
  }: {
    items: Array<ICheckoutOrderSummaryItem>
    campaign?: string
    coupon?: string
    link?: string
    upsell?: boolean
  }) => {
    if (!window.posthog) return
    const [item] = items
    const { price, discount, itemsCount, avgPrice } = calculateTotals(items)

    const event = !upsell
      ? 'Visualizar a página de compra'
      : 'Visualizar oferta de upsell'

    window.posthog.capture(event, {
      items: itemsCount,
      discount: roundTo(price - discount, 2),
      avg: roundTo(avgPrice, 2),
      total: roundTo(price, 2),
      coupon,
      campaign,
      link,
      source: getQueryParams('source'),
      product: buildProductData(item),
    })
  },

  fillUpPersonalData: ({
    items,
    total,
    campaign,
    link,
  }: {
    items: Array<ICheckoutOrderSummaryItem>
    total: number
    campaign?: string
    link?: string
  }) => {
    if (!window.posthog) return
    const [item] = items
    const { discount, itemsCount, avgPrice } = calculateTotals(items)

    window.posthog.capture('Completar dados pessoais', {
      items: itemsCount,
      discount: roundTo(discount, 2),
      avg: roundTo(avgPrice, 2),
      total: roundTo(total, 2),
      campaign,
      link,
      source: getQueryParams('source'),
      product: buildProductData(item),
    })
  },

  payment: ({
    items,
    total,
    payment,
    campaign,
    link,
    coupon,
    upsell,
  }: {
    items: Array<ICheckoutOrderSummaryItem>
    total: number
    payment: z.infer<typeof TrackingPaymentSchema>
    campaign?: string
    link?: string
    coupon?: string
    upsell?: boolean
  }) => {
    if (!window.posthog) return
    const [item] = items
    const { price, itemsCount, avgPrice } = calculateTotals(items)

    const event = !upsell
      ? 'Efetuar o pagamento'
      : 'Efetuar o pagamento no upsell'

    window.posthog.capture(event, {
      items: itemsCount,
      discount: roundTo(price - total, 2),
      avg: roundTo(avgPrice, 2),
      total: roundTo(total, 2),
      campaign,
      link,
      coupon,
      source: getQueryParams('source'),
      payment: payment.Pagamento,
      installments: payment.Parcelas,
      product: buildProductData(item),
    })
  },

  confirmation: ({
    items,
    total,
    payment,
    confirmation,
    campaign,
    coupon,
    link,
    upsell,
  }: {
    items: Array<ICheckoutOrderSummaryItem>
    total: number
    payment: z.infer<typeof TrackingPaymentSchema>
    confirmation: z.infer<typeof TrackingConfirmationSchema>
    campaign?: string
    coupon?: string
    link?: string
    upsell?: boolean
  }) => {
    if (!window.posthog) return
    const [item] = items
    const { price, itemsCount, avgPrice } = calculateTotals(items)

    const event = !upsell
      ? 'Confirmar a compra'
      : 'Confirmar a compra no upsell'

    window.posthog.capture(event, {
      items: itemsCount,
      discount: roundTo(price - total, 2),
      avg: roundTo(avgPrice, 2),
      total: roundTo(total, 2),
      campaign,
      link,
      coupon,
      source: getQueryParams('source'),
      payment: payment.Pagamento,
      installments: payment.Parcelas,
      product: buildProductData(item),
      status: confirmation.Status,
      order: confirmation.Pedido,
    })
  },
}

export default posthog
